// 后端api
import {
  getTimeMillisApi,
  getTenantConfigApi,
  getTenantPlatformDomainApi
} from '@/api/common'
// 工具函数
import { getDayjsLocalTime } from '@/utils/day'
import { addQueryToUrl, getOSSUrl } from '@/utils/common'
import dayjs from 'dayjs'
import { activityCodeDict } from 'jczh-dictionary-configs-umd'

// 默认平台配置信息
const defaultPlatformDomain = {
  portal: process.env.VUE_APP_PORTAL_URL,
  defaultPortal: process.env.VUE_APP_PORTAL_URL,
  login: process.env.VUE_APP_PASSPORT_URL,
  sourcing: process.env.VUE_APP_PLATFORM_URL,
  review: process.env.VUE_APP_EXPERT_URL,
  payCtr: process.env.VUE_APP_PAY_URL,
  expert: process.env.VUE_APP_EXPERT_URL,
  expert_h5: process.env.VUE_APP_EXPERT_H5_URL
}

const state = {
  innerWH: {}, // 浏览器窗口宽高
  // 系统时间
  systemTime: '',
  // 租户信息
  tenantId: '', // 登录平台的租户id
  tenantInfo: {}, // 租户信息，包含（租户编号、租户名称、主企业id、是否为默认租户等信息）
  platformConfig: {}, // 登录平台的配置信息，（该字段信息与运营平台配置有关）
  platformDomain: defaultPlatformDomain, // 该租户各平台的域名信息（门户：portal，登录：login，交易：sourcing，评审：review，支付中心：payCtr，增值服务平台：biddog）
  theme: '' // 平台主题（theme1和theme2）
}

const mutations = {
  SET_INNER_WH(state, val) {
    state.innerWH = val
  },
  SET_SYSTEM_TIME(state, time) {
    state.systemTime = time
  },

  SET_TENANT_ID: (state, tenantId) => {
    state.tenantId = tenantId
  },
  SET_TENANT_INFO: (state, { tenantInfo, validInfo }) => {
    state.tenantInfo = tenantInfo
    const extra = validInfo?.extra || {}
    state.platformConfig = {
      themeColor: '#1890ff,#a7d5ff,#d8ebff,#edf6ff,#f8f9fb',
      ...(validInfo?.extra || {}),
      name: validInfo.name || '精彩纵横-标前狗',
      favicon: getOSSUrl(extra.favicon),
      logo: getOSSUrl(extra.logo),
      whiteLogo: getOSSUrl(extra.whiteLogo)
    }
  },
  // 设置租户各平台的域名信息
  SET_PLATFORM_DOMAIN: (state, platformDomain) => {
    const temp = {}
    Object.keys(platformDomain).forEach((key) => {
      const item = platformDomain[key] || {}
      if (Array.isArray(item)) return

      const scheme =
        item.scheme ||
        (process.env.VUE_APP_NODE_ENV === 'production' ? 'https' : 'http')
      temp[key] = `${scheme}://${item.host?.replace(/^http(s?):\/\//, '')}`
    })

    state.platformDomain = Object.assign({}, defaultPlatformDomain, temp, {
      purchase: temp.sourcing || defaultPlatformDomain.sourcing,
      expert: temp.review || defaultPlatformDomain.expert
    })
  }
}

const actions = {
  setInnerWH({ commit }, val) {
    commit('SET_INNER_WH', val)
  },

  /**
   * 获取系统时间（不精确）
   * @param commit
   * @param axios
   * @returns {Promise<unknown>}
   */
  getSystemTime({ commit }, axios) {
    return new Promise((resolve, reject) => {
      getTimeMillisApi(axios)
        .then((res) => {
          commit('SET_SYSTEM_TIME', res.data || '')
          resolve(res.data)
        })
        .catch(() => {
          commit('SET_SYSTEM_TIME', Date.now())
          resolve(Date.now())
        })
    })
  },

  /**
   * 获取租户配置
   */
  getTenantConfig({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      const host =
        window.location.host === 'v.yingcaicheng.net:8008'
          ? 'biddog-dev.yingcaicheng.net'
          : window.location.host

      getTenantConfigApi(host)
        .then((res) => {
          commit('SET_TENANT_ID', res.data.tenantId || 1)
          commit('SET_TENANT_INFO', res.data || {})

          dispatch('getTenantPlatformDomain', res.data.tenantId).then(() => {
            resolve()
          })
        })
        .catch(() => {
          reject()
        })
    })
  },

  /**
   * 获取系统租户各平台的域名
   */
  getTenantPlatformDomain({ commit, dispatch }, tenantId) {
    return new Promise((resolve, reject) => {
      getTenantPlatformDomainApi(tenantId)
        .then((res) => {
          commit('SET_PLATFORM_DOMAIN', res.data || {})
          resolve(res.data)
        })
        .catch(() => {
          reject()
        })
    })
  },

  /**
   * 统一平台间跳转函数
   */
  toPlatform({ state }, { url, platform, path, query, target = '_blank' }) {
    window.onbeforeunload = null

    return new Promise((resolve) => {
      const urlTemp = path
        ? `${state.platformDomain[platform]}${path}`
        : state.platformDomain[platform]

      window.open(
        addQueryToUrl(url || urlTemp, {
          ...(query || {}),
          tenantId: state.tenantId
        }),
        target
      )
      resolve()
    })
  }
}

const getters = {
  // 双十一活动时间控制变量
  isActivityTime: (state) => {
    // 活动开始时间
    const startTime = dayjs.tz(
      activityCodeDict?.WUYI_2026?.startTime || '2026-05-01 00:00:00',
      'Asia/Shanghai'
    )
    // 活动结束时间
    const endTime = dayjs.tz(
      activityCodeDict?.WUYI_2026?.endTime || '2026-05-31 23:59:59',
      'Asia/Shanghai'
    )
    // 获取当前本地时间（上海时区）
    const nowTime = getDayjsLocalTime()

    const isSzyc = state?.platformSetting?.extra?.alias === 'szyc'

    // 判断当前时间是否在活动区间内（包含开始时间，不包含结束时间）
    return nowTime.isAfter(startTime) && nowTime.isBefore(endTime) && !isSzyc
  },

  platformTitle: (state) => {
    return state.platformConfig?.name || ''
  },

  // 是否为默认租户
  isDefaultTenant(state) {
    return state.tenantId === 1
  },

  // 联系方式
  contact(state) {
    const contact = state?.platformConfig?.contact || {}
    return {
      consumerHotline: contact?.consumerHotline || '400-8566-100',
      qqLink:
        contact?.qqLink || 'tencent://message/?uin=811028657&Site=&Menu=yes',
      email: contact?.consumerEmail || 'kefu@jczh.com',
      workTime: contact?.workTime || '08:30-12:00 14:00-17:30'
    }
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
